import { useMemo, useState } from "preact/hooks";
import { isMicSupported } from "../audio/mic";
import { DAILY_CARDS, type DailyCardId } from "../content/dailycards";
import { PHRASES } from "../content/phrases";
import { TWISTERS } from "../content/twisters";
import type { Card } from "../content/types";
import { applyDailyCardMultiplier, CLARITY, mulberry32 } from "../engine/clarity";
import type { PlaqueContext } from "../engine/plaques";
import { buildSession, type SessionItem, type SessionType } from "../engine/session";
import { dayStr } from "../lib/date";
import { deferredRewards, pickHeadlineReward, type Reward } from "../engine/rewards";
import type { Grade, Profile } from "../state/profile";
import {
  appendSessionLog,
  awardClarity,
  clearSessionProgress,
  completeSession,
  gradeSessionCard,
  hasSeenTip,
  loadSessionProgress,
  markTipSeen,
  profile as profileSignal,
  recordWpmPB,
  saveSessionProgress,
  setMicEnabled,
} from "../state/store";
import { useMic } from "./useMic";

const ALL_CARDS = new Map<string, Card>([...TWISTERS, ...PHRASES].map((c) => [c.id, c]));

export type BoothPhase = "mic-consent" | "lead-in" | "daily-card" | "item" | "celebration" | "summary";
const MIC_CONSENT_TIP = "mic-consent";
export type ItemPhase = "stage" | "grade" | "cleared";
export type SummaryRow = { label: string; detail?: string };

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  return h >>> 0;
}

function pickDailyCard(seed: number, today: string): DailyCardId {
  const rng = mulberry32(seed ^ hashString(today));
  const total = DAILY_CARDS.reduce((sum, c) => sum + c.weight, 0);
  let r = rng() * total;
  for (const c of DAILY_CARDS) {
    r -= c.weight;
    if (r <= 0) return c.id;
  }
  return DAILY_CARDS[DAILY_CARDS.length - 1]!.id;
}

const GRADE_LABEL = ["Again", "Hard", "Good", "Easy"] as const;

/** Mic-derived score (0-100) -> suggested grade. Always overridable — this
    only pre-highlights a button, it never grades on its own. */
function gradeFromScore(score: number): Grade {
  if (score >= 90) return 3;
  if (score >= 70) return 2;
  if (score >= 50) return 1;
  return 0;
}

export function cardContent(cardId: string): Card | undefined {
  return ALL_CARDS.get(cardId);
}

const DRILL_LABEL: Record<string, string> = {
  vowel: "Vowel Stretch",
  breath: "Breath Ladder",
  syllable: "Syllable Explosions",
};

export function labelForItem(item: SessionItem): string {
  if (item.kind === "drill") return DRILL_LABEL[item.drillId] ?? item.drillId;
  if (item.kind === "twister") return `Twister · ${item.tempo}`;
  return `Fluency · ${item.mode === "rep" ? "Flash-Rep" : "Scenario Recall"}`;
}

export function useSession(type: SessionType, onExit: () => void) {
  const today = dayStr();
  const profile: Profile = profileSignal.value;

  const resumed = useMemo(() => {
    const saved = loadSessionProgress();
    return saved && saved.day === today && saved.type === type ? saved : null;
  }, [today, type]);

  // Resuming replays the exact snapshot from when the session started —
  // recomputing buildSession() here would reshuffle "due" cards that were
  // already graded before the exit, desyncing the saved itemIndex.
  const items = useMemo(() => resumed?.items ?? buildSession(profile, type, today), [profile, type, today, resumed]);
  const dailyCardId = useMemo(
    () => (resumed?.dailyCardId as DailyCardId | null) ?? pickDailyCard(profile.seed, today),
    [profile.seed, today, resumed],
  );

  const needsMicPrompt = !resumed && isMicSupported() && !profile.settings.mic && !hasSeenTip(MIC_CONSENT_TIP);
  const [phase, setPhase] = useState<BoothPhase>(resumed ? "item" : needsMicPrompt ? "mic-consent" : "lead-in");
  const [itemIndex, setItemIndex] = useState(resumed?.itemIndex ?? 0);
  const [itemPhase, setItemPhase] = useState<ItemPhase>("stage");
  const [clarityEarned, setClarityEarned] = useState(resumed?.clarityEarned ?? 0);
  const [summary, setSummary] = useState<SummaryRow[]>([]);
  const [allEasy, setAllEasy] = useState(true);
  const [anyRetry, setAnyRetry] = useState(false);
  const [startedAt] = useState(() => Date.now());
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [headline, setHeadline] = useState<Reward | null>(null);
  const [suggestedGrade, setSuggestedGrade] = useState<Grade | undefined>(undefined);
  const [waveform, setWaveform] = useState<number[]>([]);

  const micHook = useMic(profile.settings.mic);

  const currentItem: SessionItem | undefined = items[itemIndex];

  function persistProgress(nextIndex: number, nextClarity: number) {
    saveSessionProgress({ day: today, type, itemIndex: nextIndex, clarityEarned: nextClarity, dailyCardId, items });
  }

  function enableMic() {
    setMicEnabled(true);
    markTipSeen(MIC_CONSENT_TIP);
    setPhase("lead-in");
  }

  function skipMic() {
    markTipSeen(MIC_CONSENT_TIP);
    setPhase("lead-in");
  }

  function afterLeadIn() {
    setPhase("daily-card");
  }

  function afterDailyCard() {
    setPhase("item");
    // snapshot the item list now, before any grading can shift what's due
    persistProgress(0, clarityEarned);
  }

  function completeDrillItem(baseClarity: number, label: string) {
    const awarded = applyDailyCardMultiplier(baseClarity, dailyCardId);
    awardClarity(awarded);
    const next = clarityEarned + awarded;
    setClarityEarned(next);
    setSummary((s) => [...s, { label }]);
    setWaveform((w) => [...w, awarded]);
    persistProgress(itemIndex, next);
    setItemPhase("cleared");
  }

  function completeCardStage(voiceScore?: number) {
    setSuggestedGrade(voiceScore === undefined ? undefined : gradeFromScore(voiceScore));
    setItemPhase("grade");
  }

  function grade(g: Grade) {
    const item = currentItem;
    if (!item || item.kind === "drill") return;
    setSuggestedGrade(undefined);
    const result = gradeSessionCard(item.cardId, g);

    if (g < 2) setAnyRetry(true);
    if (g !== 3) setAllEasy(false);

    let base = g === 0 ? CLARITY.reviewAgain : g === 1 ? CLARITY.reviewHard : result.wasNew ? CLARITY.newCardIntroduced : CLARITY.reviewGood;
    if (result.justMastered) base += CLARITY.firstMastery;
    if (g >= 2 && item.kind === "twister") {
      const content = cardContent(item.cardId);
      if (content) recordWpmPB(item.cardId, content.targetWpm[item.tempo]);
    }
    const awarded = applyDailyCardMultiplier(base, dailyCardId);
    awardClarity(awarded);

    const next = clarityEarned + awarded;
    setClarityEarned(next);
    const content = cardContent(item.cardId);
    setSummary((s) => [...s, { label: content?.text ?? item.cardId, detail: GRADE_LABEL[g] }]);
    setWaveform((w) => [...w, awarded]);
    persistProgress(itemIndex, next);
    setItemPhase("cleared");
  }

  function advance() {
    if (itemIndex + 1 >= items.length) {
      finish();
      return;
    }
    const nextIndex = itemIndex + 1;
    setItemIndex(nextIndex);
    setItemPhase("stage");
    persistProgress(nextIndex, clarityEarned);
  }

  function finish() {
    const bonus = CLARITY.sessionComplete;
    awardClarity(bonus);
    const total = clarityEarned + bonus;
    setClarityEarned(total);

    const ctx: PlaqueContext = {
      sessionHour: new Date().getHours(),
      sessionAllEasy: allEasy,
      sessionAllCleanNoRetries: allEasy && !anyRetry,
      sessionDailyCardId: dailyCardId,
      sessionDailyCardClean: allEasy,
    };
    const secondsSpent = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
    const earnedRewards = completeSession({
      closesRing: type === "daily",
      lang: profile.identity.primaryLang,
      clarityAwarded: bonus,
      secondsSpent,
      ctx,
    });

    const headlineReward = pickHeadlineReward(earnedRewards);
    setHeadline(headlineReward);
    setRewards(headlineReward ? deferredRewards(earnedRewards, headlineReward) : earnedRewards);

    appendSessionLog({
      at: new Date().toISOString(),
      clarity: total,
      seconds: secondsSpent,
      itemsCleared: items.length,
      waveform,
      dailyCard: dailyCardId,
    });

    clearSessionProgress();
    setPhase(headlineReward ? "celebration" : "summary");
  }

  function afterCelebration() {
    setPhase("summary");
  }

  function exit() {
    // progress already persisted per-item — nothing to lose by leaving now
    onExit();
  }

  return {
    items,
    phase,
    itemIndex,
    itemPhase,
    currentItem,
    dailyCardId,
    clarityEarned,
    summary,
    rewards,
    headline,
    waveform,
    suggestedGrade,
    mic: micHook.mic,
    micState: micHook.state,
    micThreshold: micHook.threshold,
    enableMic,
    skipMic,
    afterLeadIn,
    afterDailyCard,
    afterCelebration,
    completeDrillItem,
    completeCardStage,
    grade,
    advance,
    exit,
  };
}
